import React from 'react';
import { Button, Typography, Space, theme } from 'antd';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { useToken } = theme;

export interface EmptyStateAction {
  label: string;
  type?: 'primary' | 'default' | 'link';
  onClick?: () => void;
  href?: string;
}

export interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  actions?: EmptyStateAction[];
}

/**
 * 空状態を表示する共通コンポーネント
 * データが0件の場合や、前提条件が満たされていない場合に使用
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  actions = [],
}) => {
  const navigate = useNavigate();
  const { token } = useToken();

  const handleActionClick = (action: EmptyStateAction) => {
    if (action.onClick) {
      action.onClick();
    } else if (action.href) {
      navigate(action.href);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 24px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          fontSize: 64,
          marginBottom: 24,
          color: token.colorTextQuaternary,
          lineHeight: 1,
        }}
      >
        {icon}
      </div>

      <Title
        level={4}
        style={{
          marginBottom: description ? 8 : 24,
        }}
      >
        {title}
      </Title>

      {description && (
        <Text
          type="secondary"
          style={{
            display: 'block',
            marginBottom: 24,
            maxWidth: 400,
            lineHeight: 1.6,
          }}
        >
          {description}
        </Text>
      )}

      {actions.length > 0 && (
        <Space size="middle">
          {actions.map((action, index) => (
            <Button
              key={index}
              type={action.type || (index === 0 ? 'primary' : 'default')}
              onClick={() => handleActionClick(action)}
            >
              {action.label}
            </Button>
          ))}
        </Space>
      )}
    </div>
  );
};

export default EmptyState;
